import { post } from "@src/appV2/api/api";
import { API_TYPES } from "@src/appV2/api/constants";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

const ROLLOUT_KEY = "workerApp";

// Zod schemas for Firebase API
// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const generateDeepLinkParamsSchema = z.object({
  link: z.string(),
  skipPreview: z.boolean(),
  options: z
    .object({
      androidFallbackLink: z.string().optional(),
      iosFallbackLink: z.string().optional(),
      iosIpadFallbackLink: z.string().optional(),
    })
    .optional(),
});

// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const generateDeepLinkResponseSchema = z.object({
  shortLink: z.string(),
});

// Zod schemas for backend deep link
const backendDeepLinkRequestSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.deepLink),
    attributes: z.object({
      path: z.string().refine((path) => path.startsWith("/"), {
        message: "Path must start with a slash",
      }),
      rolloutKey: z.string().optional(),
    }),
  }),
});

const backendDeepLinkResponseSchema = z.object({
  type: z.literal(API_TYPES.deepLink),
  attributes: z.object({
    deepLink: z.string(),
  }),
  id: z.string(),
});

export type GenerateDeepLinkParams = z.infer<typeof generateDeepLinkParamsSchema>;
export type GenerateDeepLinkResponse = z.infer<typeof generateDeepLinkResponseSchema>;

async function generateDeepLinkViaBackendMain(link: string): Promise<string> {
  const url = new URL(link);
  const path = url.pathname + url.search;

  const response = await post({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/deep-links`,
    data: {
      data: {
        type: API_TYPES.deepLink,
        attributes: {
          path,
          rolloutKey: ROLLOUT_KEY,
        },
      },
    },
    requestSchema: backendDeepLinkRequestSchema,
    responseSchema: backendDeepLinkResponseSchema,
  });

  return response.data.attributes.deepLink;
}

export function useGenerateDeepLink(
  options: UseMutationOptions<string, AxiosError, GenerateDeepLinkParams> = {}
): UseMutationResult<string, AxiosError, GenerateDeepLinkParams> {
  return useMutation({
    mutationFn: async (params: GenerateDeepLinkParams) => {
      try {
        const { link } = params;
        return await generateDeepLinkViaBackendMain(link);
      } catch (error) {
        logError(APP_V2_APP_EVENTS.DEEP_LINK_GENERATION_FAILED, {
          error,
        });
        throw error;
      }
    },
    ...options,
  });
}
